import React, { useState, useEffect, useCallback } from 'react';
import { schedulesApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Trash, PencilSimple, Warning, House, Briefcase, Phone, CalendarX, Repeat, CheckSquare, ArrowsClockwise, Lightning } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import { teamsApi } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCHEDULE_TYPES = [
  { value: 'office', label: 'Ufficio', icon: House, color: '#0A0A0A' },
  { value: 'smart_working', label: 'Smart Working', icon: Briefcase, color: '#002FA7' },
  { value: 'leave', label: 'Ferie/Assenza', icon: CalendarX, color: '#F59E0B' },
  { value: 'on_call', label: 'Reperibilità', icon: Phone, color: '#FF2A00' },
];
const WEEKDAYS = [
  { value: 0, label: 'Lun' }, { value: 1, label: 'Mar' }, { value: 2, label: 'Mer' },
  { value: 3, label: 'Gio' }, { value: 4, label: 'Ven' },
];

const ScheduleBadge = ({ type }) => {
  const t = SCHEDULE_TYPES.find((s) => s.value === type) || SCHEDULE_TYPES[0];
  const Icon = t.icon;
  return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-bold uppercase tracking-wider border" style={{ borderColor: t.color, color: t.color, backgroundColor: `${t.color}10` }}><Icon size={12} weight="bold" />{t.label}</span>;
};
const StatusBadge = ({ status }) => {
  const m = { pending: { c: 'border-[#F59E0B] text-[#F59E0B] bg-[#F59E0B]/5', l: 'In Attesa' }, approved: { c: 'border-[#22C55E] text-[#22C55E] bg-[#22C55E]/5', l: 'Confermato' }, rejected: { c: 'border-[#FF2A00] text-[#FF2A00] bg-[#FF2A00]/5', l: 'Rifiutato' } };
  const s = m[status] || m.pending;
  return <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider border ${s.c}`}>{s.l}</span>;
};

const SchedulesPage = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRecurringOpen, setIsRecurringOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showPast, setShowPast] = useState(false);
  const [formData, setFormData] = useState({
    schedule_type: 'office', start_date: '', end_date: '', notes: '',
    target_user_id: '', assign_mode: 'team', target_team_id: '',
  });
  const [recurringForm, setRecurringForm] = useState({
    schedule_type: 'office', start_date: '', end_date: '', notes: '',
    target_team_id: '', target_user_id: '', assign_mode: 'team',
    days_of_week: [0, 1, 2, 3, 4], // Mon-Fri default
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isRotationOpen, setIsRotationOpen] = useState(false);
  const [rotationForm, setRotationForm] = useState({
    team_id: '', start_date: '', weeks: 4, days_per_shift: 7, user_ids: [],
  });
  const [rotationSubmitting, setRotationSubmitting] = useState(false);
  const [isAutoPlanOpen, setIsAutoPlanOpen] = useState(false);
  const [autoPlanForm, setAutoPlanForm] = useState({
    team_id: '', month: new Date().getMonth() + 1, year: new Date().getFullYear(),
    office_days_per_week: 3, sw_days_per_week: 2,
  });
  const [autoPlanResult, setAutoPlanResult] = useState(null);
  const [autoPlanLoading, setAutoPlanLoading] = useState(false);
  const [applyingPlan, setApplyingPlan] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [schedulesRes, membersRes, teamsRes] = await Promise.all([
        schedulesApi.getAll({}),
        axios.get(`${API_URL}/api/team-members`, { withCredentials: true }),
        teamsApi.getAll(),
      ]);
      setSchedules(schedulesRes.data);
      setTeamMembers(membersRes.data);
      setTeams(teamsRes.data);
    } catch (error) { console.error(error); toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setFormData({ schedule_type: 'office', start_date: '', end_date: '', notes: '', target_user_id: '', assign_mode: 'team', target_team_id: '' });
    setEditingSchedule(null); setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSubmitting(true);
    try {
      const payload = {
        schedule_type: formData.schedule_type, start_date: formData.start_date, end_date: formData.end_date, notes: formData.notes,
        target_user_id: formData.assign_mode === 'person' ? (formData.target_user_id || null) : null,
        target_team_id: formData.assign_mode === 'team' ? (formData.target_team_id || null) : null,
      };
      if (editingSchedule) {
        await schedulesApi.update(editingSchedule.id, payload);
        toast.success('Turno aggiornato');
      } else {
        const res = await schedulesApi.create(payload);
        toast.success(res.data.count ? `Turni assegnati a ${res.data.count} membri` : 'Turno assegnato');
      }
      setIsDialogOpen(false); resetForm(); fetchData();
    } catch (err) { setError(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const handleRecurringSubmit = async (e) => {
    e.preventDefault(); setError(''); setSubmitting(true);
    try {
      const payload = {
        schedule_type: recurringForm.schedule_type,
        start_date: recurringForm.start_date, end_date: recurringForm.end_date,
        notes: recurringForm.notes, days_of_week: recurringForm.days_of_week,
        target_team_id: recurringForm.assign_mode === 'team' ? (recurringForm.target_team_id || null) : null,
        target_user_id: recurringForm.assign_mode === 'person' ? (recurringForm.target_user_id || null) : null,
      };
      const { data } = await schedulesApi.createRecurring(payload);
      toast.success(`Creati ${data.count} turni ricorrenti`);
      setIsRecurringOpen(false); fetchData();
    } catch (err) { setError(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo turno?')) return;
    try { await schedulesApi.delete(id); toast.success('Eliminato'); fetchData(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminare ${selectedIds.size} turni selezionati?`)) return;
    try {
      await schedulesApi.bulkDelete([...selectedIds]);
      toast.success(`${selectedIds.size} turni eliminati`);
      setSelectedIds(new Set()); fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };
  const toggleSelectAll = () => {
    const list = filteredSchedules;
    if (selectedIds.size === list.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(list.map(s => s.id)));
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const activeSchedules = schedules.filter(s => s.end_date >= todayStr);
  const pastSchedules = schedules.filter(s => s.end_date < todayStr);
  const filteredSchedules = showPast ? pastSchedules : activeSchedules;

  const toggleDay = (day) => {
    const days = [...recurringForm.days_of_week];
    const idx = days.indexOf(day);
    idx >= 0 ? days.splice(idx, 1) : days.push(day);
    setRecurringForm({ ...recurringForm, days_of_week: days.sort() });
  };

  const handleRotationSubmit = async (e) => {
    e.preventDefault();
    if (rotationForm.user_ids.length === 0) { toast.error('Seleziona almeno un membro'); return; }
    if (!rotationForm.start_date) { toast.error('Seleziona data inizio'); return; }
    setRotationSubmitting(true);
    try {
      const { data } = await schedulesApi.onCallRotation(rotationForm);
      toast.success(data.message);
      setIsRotationOpen(false);
      setRotationForm({ team_id: '', start_date: '', weeks: 4, days_per_shift: 7, user_ids: [] });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRotationSubmitting(false); }
  };

  const toggleRotationUser = (userId) => {
    const ids = [...rotationForm.user_ids];
    const idx = ids.indexOf(userId);
    if (idx >= 0) ids.splice(idx, 1);
    else ids.push(userId);
    setRotationForm({ ...rotationForm, user_ids: ids });
  };

  const moveRotationUser = (idx, direction) => {
    const ids = [...rotationForm.user_ids];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= ids.length) return;
    [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
    setRotationForm({ ...rotationForm, user_ids: ids });
  };

  const MONTHS_IT = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

  const handleAutoPlanGenerate = async () => {
    if (!autoPlanForm.team_id) { toast.error('Seleziona un team'); return; }
    setAutoPlanLoading(true);
    setAutoPlanResult(null);
    try {
      const { data } = await schedulesApi.autoPlan(autoPlanForm);
      setAutoPlanResult(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAutoPlanLoading(false); }
  };

  const handleAutoPlanApply = async () => {
    if (!autoPlanResult?.suggestions?.length) return;
    setApplyingPlan(true);
    try {
      const { data } = await schedulesApi.applyAutoPlan(autoPlanResult.suggestions);
      toast.success(`${data.created} turni creati${data.skipped > 0 ? `, ${data.skipped} saltati (conflitto)` : ''}`);
      setIsAutoPlanOpen(false);
      setAutoPlanResult(null);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setApplyingPlan(false); }
  };

  return (
    <div data-testid="schedules-page">
      <Toaster position="top-right" richColors />

      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Gestione Turni</h1>
          <p className="text-[#52525B] mt-1 text-sm">Assegna turni singoli o ricorrenti ai team</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedIds.size > 0 && (
            <Button onClick={handleBulkDelete} className="bg-[#FF2A00] text-white px-5 py-2.5 font-medium hover:bg-[#D92400] rounded-none" data-testid="bulk-delete-btn">
              <Trash size={18} weight="bold" className="mr-2" />
              Elimina ({selectedIds.size})
            </Button>
          )}
          <Button onClick={() => { setIsAutoPlanOpen(true); setAutoPlanResult(null); if (teams.length > 0 && !autoPlanForm.team_id) setAutoPlanForm(f => ({...f, team_id: teams[0]?.id || ''})); }}
            variant="outline" className="border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white px-5 py-2.5 font-medium rounded-none" data-testid="auto-plan-btn">
            <Lightning size={18} weight="bold" className="mr-2" /> Auto-Pianifica
          </Button>
          <Dialog open={isRecurringOpen} onOpenChange={setIsRecurringOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setError(''); setIsRecurringOpen(true); }} variant="outline"
                className="border-[#0A0A0A] text-[#0A0A0A] px-5 py-2.5 font-medium hover:bg-[#F4F4F5] rounded-none" data-testid="recurring-schedule-btn">
                <Repeat size={18} weight="bold" className="mr-2" /> Turno Ricorrente
              </Button>
            </DialogTrigger>
          </Dialog>
          <Dialog open={isRotationOpen} onOpenChange={setIsRotationOpen}>
            <Button onClick={() => { setIsRotationOpen(true); if (teams.length > 0 && !rotationForm.team_id) setRotationForm({...rotationForm, team_id: teams[0]?.id || ''}); }}
              className="bg-[#FF2A00] text-white px-5 py-2.5 font-medium hover:bg-[#D92400] rounded-none" data-testid="rotation-btn">
              <ArrowsClockwise size={18} weight="bold" className="mr-2" /> Rotazione Reperibilità
            </Button>
            <DialogContent className="sm:max-w-lg rounded-none border border-[#FF2A00] max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-xl font-bold text-[#FF2A00]">Rotazione Reperibilità Settimanale</DialogTitle></DialogHeader>
              <p className="text-sm text-[#52525B] mb-4">Crea turni di reperibilità a rotazione: ogni membro selezionato sarà reperibile per il numero di giorni indicato, uno dopo l'altro nell'ordine definito.</p>
              <form onSubmit={handleRotationSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Team</Label>
                  <Select value={rotationForm.team_id} onValueChange={v => setRotationForm({...rotationForm, team_id: v, user_ids: []})}>
                    <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="rotation-team"><SelectValue placeholder="Seleziona team" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {teams.map(t => <SelectItem key={t.id} value={t.id} className="rounded-none">{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Data Inizio</Label>
                    <Input type="date" value={rotationForm.start_date}
                      onChange={e => setRotationForm({...rotationForm, start_date: e.target.value})}
                      className="rounded-none border-[#E4E4E7]" data-testid="rotation-start" required />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Settimane</Label>
                    <Input type="number" min={1} max={52} value={rotationForm.weeks}
                      onChange={e => setRotationForm({...rotationForm, weeks: parseInt(e.target.value) || 4})}
                      className="rounded-none border-[#E4E4E7]" data-testid="rotation-weeks" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Giorni per turno</Label>
                  <Input type="number" min={1} max={14} value={rotationForm.days_per_shift}
                    onChange={e => setRotationForm({...rotationForm, days_per_shift: parseInt(e.target.value) || 7})}
                    className="rounded-none border-[#E4E4E7] w-24" data-testid="rotation-days" />
                  <p className="text-xs text-[#A1A1AA] mt-1">7 = rotazione settimanale</p>
                </div>
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">
                    Membri (ordine = ordine rotazione) — {rotationForm.user_ids.length} selezionati
                  </Label>
                  <div className="border border-[#E4E4E7] max-h-48 overflow-y-auto divide-y divide-[#E4E4E7]">
                    {teamMembers.filter(m => !rotationForm.team_id || (m.team_ids || []).includes(rotationForm.team_id)).map(m => {
                      const selected = rotationForm.user_ids.includes(m.id);
                      const orderIdx = rotationForm.user_ids.indexOf(m.id);
                      return (
                        <div key={m.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#FAFAFA] ${selected ? 'bg-[#FF2A00]/5' : ''}`}
                          onClick={() => toggleRotationUser(m.id)} data-testid={`rotation-member-${m.id}`}>
                          <div className={`w-6 h-6 flex items-center justify-center text-[10px] font-black ${selected ? 'bg-[#FF2A00] text-white' : 'bg-[#E4E4E7] text-[#52525B]'}`}>
                            {selected ? orderIdx + 1 : '-'}
                          </div>
                          <span className="text-sm flex-1">{m.name}</span>
                          {selected && (
                            <div className="flex gap-1">
                              <button type="button" onClick={e => { e.stopPropagation(); moveRotationUser(orderIdx, -1); }}
                                className="text-[10px] px-1 border border-[#E4E4E7] hover:bg-[#E4E4E7]">↑</button>
                              <button type="button" onClick={e => { e.stopPropagation(); moveRotationUser(orderIdx, 1); }}
                                className="text-[10px] px-1 border border-[#E4E4E7] hover:bg-[#E4E4E7]">↓</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {rotationForm.user_ids.length > 0 && rotationForm.start_date && (
                  <div className="bg-[#FF2A00]/5 border border-[#FF2A00]/30 p-3">
                    <p className="text-xs font-bold text-[#FF2A00] mb-1">Anteprima rotazione:</p>
                    {rotationForm.user_ids.slice(0, rotationForm.weeks).map((uid, i) => {
                      const m = teamMembers.find(x => x.id === uid);
                      const start = new Date(rotationForm.start_date);
                      start.setDate(start.getDate() + i * rotationForm.days_per_shift);
                      const end = new Date(start);
                      end.setDate(end.getDate() + rotationForm.days_per_shift - 1);
                      return (
                        <p key={i} className="text-xs text-[#52525B] font-mono">
                          Sett. {i + 1}: <strong>{m?.name || uid}</strong> — {start.toLocaleDateString('it-IT')} → {end.toLocaleDateString('it-IT')}
                        </p>
                      );
                    })}
                    {rotationForm.weeks > rotationForm.user_ids.length && (
                      <p className="text-[10px] text-[#A1A1AA] mt-1">...poi la rotazione ricomincia dal primo membro</p>
                    )}
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsRotationOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
                  <Button type="submit" disabled={rotationSubmitting || rotationForm.user_ids.length === 0}
                    className="flex-1 bg-[#FF2A00] text-white hover:bg-[#D92400] rounded-none" data-testid="confirm-rotation-btn">
                    {rotationSubmitting ? 'Creazione...' : 'Crea Rotazione'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={isRecurringOpen} onOpenChange={setIsRecurringOpen}>
            <DialogContent className="sm:max-w-lg rounded-none border border-[#0A0A0A] max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Turno Ricorrente</DialogTitle></DialogHeader>
              {error && <div className="bg-[#FF2A00]/5 border border-[#FF2A00] p-3 flex items-start gap-2"><Warning className="text-[#FF2A00] flex-shrink-0 mt-0.5" size={16} weight="bold" /><p className="text-[#FF2A00] text-sm">{error}</p></div>}
              <form onSubmit={handleRecurringSubmit} className="space-y-4 mt-2">
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Modalità</Label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setRecurringForm({...recurringForm, assign_mode: 'team'})}
                      className={`flex-1 py-2 px-4 text-sm font-medium border transition-colors ${recurringForm.assign_mode === 'team' ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#52525B] border-[#E4E4E7]'}`}>Intero Team</button>
                    <button type="button" onClick={() => setRecurringForm({...recurringForm, assign_mode: 'person'})}
                      className={`flex-1 py-2 px-4 text-sm font-medium border transition-colors ${recurringForm.assign_mode === 'person' ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#52525B] border-[#E4E4E7]'}`}>Singolo</button>
                  </div>
                </div>
                {recurringForm.assign_mode === 'team' ? (
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Team</Label>
                    <Select value={recurringForm.target_team_id} onValueChange={v => setRecurringForm({...recurringForm, target_team_id: v})}>
                      <SelectTrigger className="rounded-none border-[#E4E4E7]"><SelectValue placeholder="Seleziona team" /></SelectTrigger>
                      <SelectContent className="rounded-none">{teams.map(t => <SelectItem key={t.id} value={t.id} className="rounded-none">{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Dipendente</Label>
                    <Select value={recurringForm.target_user_id} onValueChange={v => setRecurringForm({...recurringForm, target_user_id: v})}>
                      <SelectTrigger className="rounded-none border-[#E4E4E7]"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent className="rounded-none">{teamMembers.map(m => <SelectItem key={m.id} value={m.id} className="rounded-none">{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Tipo Turno</Label>
                  <Select value={recurringForm.schedule_type} onValueChange={v => setRecurringForm({...recurringForm, schedule_type: v})}>
                    <SelectTrigger className="rounded-none border-[#E4E4E7]"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">{SCHEDULE_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="rounded-none"><div className="flex items-center gap-2"><t.icon size={16} style={{ color: t.color }} />{t.label}</div></SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-3 block">Giorni della Settimana</Label>
                  <div className="flex gap-2">
                    {WEEKDAYS.map(d => (
                      <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
                        className={`w-12 h-12 flex items-center justify-center text-sm font-bold border transition-colors ${
                          recurringForm.days_of_week.includes(d.value) ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#0A0A0A]'
                        }`} data-testid={`day-${d.label}`}>{d.label}</button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Data Inizio</Label>
                    <Input type="date" value={recurringForm.start_date} onChange={e => setRecurringForm({...recurringForm, start_date: e.target.value})} className="rounded-none border-[#E4E4E7]" required /></div>
                  <div><Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Data Fine</Label>
                    <Input type="date" value={recurringForm.end_date} onChange={e => setRecurringForm({...recurringForm, end_date: e.target.value})} className="rounded-none border-[#E4E4E7]" required /></div>
                </div>
                <div><Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Note</Label>
                  <Textarea value={recurringForm.notes} onChange={e => setRecurringForm({...recurringForm, notes: e.target.value})} className="rounded-none border-[#E4E4E7] min-h-12" /></div>
                <p className="text-xs text-[#52525B] border-t border-[#E4E4E7] pt-3">I turni verranno creati per ogni giorno selezionato nel periodo indicato. I conflitti verranno saltati automaticamente.</p>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setIsRecurringOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 bg-[#002FA7] text-white hover:bg-[#00278A] rounded-none" data-testid="submit-recurring-btn">
                    {submitting ? 'Creazione...' : 'Crea Turni Ricorrenti'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}
                className="bg-[#002FA7] text-white px-6 py-2.5 font-medium hover:bg-[#00278A] rounded-none" data-testid="new-schedule-btn">
                <Plus size={20} weight="bold" className="mr-2" /> Assegna Turno
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-none border border-[#0A0A0A] max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-xl font-bold">{editingSchedule ? 'Modifica Turno' : 'Assegna Turno'}</DialogTitle></DialogHeader>
              {error && <div className="bg-[#FF2A00]/5 border border-[#FF2A00] p-3 flex items-start gap-2"><Warning className="text-[#FF2A00] flex-shrink-0 mt-0.5" size={16} weight="bold" /><p className="text-[#FF2A00] text-sm">{error}</p></div>}
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {!editingSchedule && (
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Modalità Assegnazione</Label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setFormData({...formData, assign_mode: 'team'})}
                        className={`flex-1 py-2 px-4 text-sm font-medium border transition-colors ${formData.assign_mode === 'team' ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#52525B] border-[#E4E4E7]'}`}
                        data-testid="mode-team-btn">Intero Team</button>
                      <button type="button" onClick={() => setFormData({...formData, assign_mode: 'person'})}
                        className={`flex-1 py-2 px-4 text-sm font-medium border transition-colors ${formData.assign_mode === 'person' ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#52525B] border-[#E4E4E7]'}`}
                        data-testid="mode-person-btn">Singolo Dipendente</button>
                    </div>
                  </div>
                )}
                {formData.assign_mode === 'team' && !editingSchedule && (
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Team</Label>
                    <Select value={formData.target_team_id} onValueChange={v => setFormData({...formData, target_team_id: v})}>
                      <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="target-team-select"><SelectValue placeholder="Seleziona team" /></SelectTrigger>
                      <SelectContent className="rounded-none">{teams.map(t => <SelectItem key={t.id} value={t.id} className="rounded-none">{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {(formData.assign_mode === 'person' || editingSchedule) && (
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Dipendente</Label>
                    <Select value={formData.target_user_id} onValueChange={v => setFormData({...formData, target_user_id: v})}>
                      <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="target-user-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent className="rounded-none">{teamMembers.map(m => <SelectItem key={m.id} value={m.id} className="rounded-none">{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Tipo Turno</Label>
                  <Select value={formData.schedule_type} onValueChange={v => setFormData({...formData, schedule_type: v})}>
                    <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="schedule-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">{SCHEDULE_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="rounded-none"><div className="flex items-center gap-2"><t.icon size={16} style={{ color: t.color }} />{t.label}</div></SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Data Inizio</Label>
                    <Input type="date" value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} className="rounded-none border-[#E4E4E7]" data-testid="start-date-input" required /></div>
                  <div><Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Data Fine</Label>
                    <Input type="date" value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} className="rounded-none border-[#E4E4E7]" data-testid="end-date-input" required /></div>
                </div>
                <div><Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Note (Facoltativo)</Label>
                  <Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} className="rounded-none border-[#E4E4E7] min-h-12" data-testid="notes-input" /></div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 bg-[#002FA7] text-white hover:bg-[#00278A] rounded-none" data-testid="submit-schedule-btn">
                    {submitting ? 'Salvataggio...' : editingSchedule ? 'Aggiorna' : 'Assegna'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>
      ) : schedules.length === 0 ? (
        <div className="border border-[#E4E4E7] p-12 text-center">
          <CalendarX size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <h3 className="font-heading font-bold text-lg mb-2">Nessun turno</h3>
          <p className="text-[#52525B] mb-4">Assegna turni singoli o ricorrenti al team.</p>
        </div>
      ) : (
        <>
          {/* Tabs: Active / Past */}
          <div className="flex items-center gap-0 mb-4 border-b border-[#E4E4E7]" data-testid="schedule-tabs">
            <button onClick={() => { setShowPast(false); setSelectedIds(new Set()); }}
              className={`px-5 py-2.5 text-sm font-bold transition-colors border-b-2 ${!showPast ? 'border-[#0A0A0A] text-[#0A0A0A]' : 'border-transparent text-[#A1A1AA] hover:text-[#52525B]'}`}
              data-testid="tab-active">
              Attivi / Futuri <span className="ml-1 text-xs font-normal text-[#A1A1AA]">({activeSchedules.length})</span>
            </button>
            <button onClick={() => { setShowPast(true); setSelectedIds(new Set()); }}
              className={`px-5 py-2.5 text-sm font-bold transition-colors border-b-2 ${showPast ? 'border-[#0A0A0A] text-[#0A0A0A]' : 'border-transparent text-[#A1A1AA] hover:text-[#52525B]'}`}
              data-testid="tab-past">
              Passati <span className="ml-1 text-xs font-normal text-[#A1A1AA]">({pastSchedules.length})</span>
            </button>
          </div>

          {filteredSchedules.length === 0 ? (
            <div className="border border-[#E4E4E7] p-8 text-center">
              <p className="text-[#A1A1AA] text-sm">{showPast ? 'Nessun turno passato' : 'Nessun turno attivo o futuro'}</p>
            </div>
          ) : (
          <div className="border border-[#E4E4E7] overflow-x-auto">
            <table className="w-full data-table min-w-[700px]">
              <thead>
                <tr className="border-b-2 border-[#0A0A0A] bg-white">
                  <th className="py-4 px-3 text-left w-10">
                    <Checkbox checked={selectedIds.size === filteredSchedules.length && filteredSchedules.length > 0}
                      onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                  </th>
                  <th className="py-4 px-4 text-left">Dipendente</th>
                  <th className="py-4 px-4 text-left">Tipo</th>
                  <th className="py-4 px-4 text-left">Data Inizio</th>
                  <th className="py-4 px-4 text-left">Data Fine</th>
                  <th className="py-4 px-4 text-left">Stato</th>
                  <th className="py-4 px-4 text-left">Note</th>
                  {!showPast && <th className="py-4 px-4 text-right">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {filteredSchedules.map(s => (
                  <tr key={s.id} className={`border-b border-[#E4E4E7] hover:bg-[#F4F4F5] transition-colors ${selectedIds.has(s.id) ? 'bg-[#002FA7]/5' : ''}`}
                    data-testid={`schedule-row-${s.id}`}>
                    <td className="py-3 px-3">
                      <Checkbox checked={selectedIds.has(s.id)} onCheckedChange={() => toggleSelect(s.id)} />
                    </td>
                    <td className="py-3 px-4 font-medium">{s.user_name}</td>
                    <td className="py-3 px-4"><ScheduleBadge type={s.schedule_type} /></td>
                    <td className="py-3 px-4 font-mono text-sm">{s.start_date}</td>
                    <td className="py-3 px-4 font-mono text-sm">{s.end_date}</td>
                    <td className="py-3 px-4"><StatusBadge status={s.status} /></td>
                    <td className="py-3 px-4 text-sm text-[#52525B] max-w-xs truncate">{s.notes || '-'}</td>
                    {!showPast && (
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => { setEditingSchedule(s); setFormData({ schedule_type: s.schedule_type, start_date: s.start_date, end_date: s.end_date, notes: s.notes || '', target_user_id: s.user_id || '', assign_mode: 'person', target_team_id: '' }); setIsDialogOpen(true); }}
                            className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#0A0A0A] transition-colors" title="Modifica"><PencilSimple size={16} weight="bold" /></button>
                          <button onClick={() => handleDelete(s.id)}
                            className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors" title="Elimina"><Trash size={16} weight="bold" /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}

      {/* Auto-Plan Dialog */}
      <Dialog open={isAutoPlanOpen} onOpenChange={setIsAutoPlanOpen}>
        <DialogContent className="sm:max-w-2xl rounded-none border border-[#22C55E] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
            <Lightning size={20} weight="bold" className="text-[#22C55E]" /> Auto-Pianificazione Mensile
          </DialogTitle></DialogHeader>
          <p className="text-sm text-[#52525B] mb-4">Genera automaticamente un piano mensile per un team, rispettando i vincoli esistenti (ferie, regole di esclusione, festivi).</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Team</Label>
                <Select value={autoPlanForm.team_id} onValueChange={v => setAutoPlanForm({...autoPlanForm, team_id: v})}>
                  <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="autoplan-team"><SelectValue placeholder="Seleziona team" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {teams.map(t => <SelectItem key={t.id} value={t.id} className="rounded-none">{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Mese</Label>
                <Select value={String(autoPlanForm.month)} onValueChange={v => setAutoPlanForm({...autoPlanForm, month: parseInt(v)})}>
                  <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="autoplan-month"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {MONTHS_IT.map((m, i) => <SelectItem key={i+1} value={String(i+1)} className="rounded-none">{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Anno</Label>
                <Input type="number" min={2024} max={2030} value={autoPlanForm.year}
                  onChange={e => setAutoPlanForm({...autoPlanForm, year: parseInt(e.target.value) || new Date().getFullYear()})}
                  className="rounded-none border-[#E4E4E7]" data-testid="autoplan-year" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Gg. Ufficio / Sett.</Label>
                <Input type="number" min={0} max={5} value={autoPlanForm.office_days_per_week}
                  onChange={e => setAutoPlanForm({...autoPlanForm, office_days_per_week: parseInt(e.target.value) || 0})}
                  className="rounded-none border-[#E4E4E7]" data-testid="autoplan-office" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Gg. SW / Sett.</Label>
                <Input type="number" min={0} max={5} value={autoPlanForm.sw_days_per_week}
                  onChange={e => setAutoPlanForm({...autoPlanForm, sw_days_per_week: parseInt(e.target.value) || 0})}
                  className="rounded-none border-[#E4E4E7]" data-testid="autoplan-sw" />
              </div>
            </div>

            <Button onClick={handleAutoPlanGenerate} disabled={autoPlanLoading || !autoPlanForm.team_id}
              className="w-full bg-[#22C55E] text-white hover:bg-[#16A34A] rounded-none" data-testid="generate-plan-btn">
              {autoPlanLoading ? 'Generazione...' : 'Genera Piano'}
            </Button>

            {autoPlanResult && (
              <div className="space-y-3">
                <div className="bg-[#22C55E]/5 border border-[#22C55E]/30 p-3">
                  <p className="text-sm font-bold text-[#22C55E]">
                    Piano per {autoPlanResult.team_name} - {MONTHS_IT[autoPlanResult.month - 1]} {autoPlanResult.year}
                  </p>
                  <p className="text-xs text-[#52525B] mt-1">{autoPlanResult.members_count} membri, {autoPlanResult.total_suggestions} turni suggeriti</p>
                </div>
                
                {/* Summary by member */}
                <div className="border border-[#E4E4E7] max-h-60 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white border-b border-[#E4E4E7]">
                      <tr>
                        <th className="py-2 px-3 text-left font-bold">Dipendente</th>
                        <th className="py-2 px-3 text-center font-bold">Ufficio</th>
                        <th className="py-2 px-3 text-center font-bold">SW</th>
                        <th className="py-2 px-3 text-center font-bold">Totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const byUser = {};
                        autoPlanResult.suggestions.forEach(s => {
                          if (!byUser[s.user_id]) byUser[s.user_id] = { name: s.user_name, office: 0, sw: 0 };
                          if (s.schedule_type === 'office') byUser[s.user_id].office++;
                          else if (s.schedule_type === 'smart_working') byUser[s.user_id].sw++;
                        });
                        return Object.entries(byUser).map(([uid, data]) => (
                          <tr key={uid} className="border-b border-[#E4E4E7]">
                            <td className="py-1.5 px-3 font-medium">{data.name}</td>
                            <td className="py-1.5 px-3 text-center"><span className="inline-flex items-center justify-center w-6 h-6 bg-[#22C55E] text-white font-bold text-[10px]">{data.office}</span></td>
                            <td className="py-1.5 px-3 text-center"><span className="inline-flex items-center justify-center w-6 h-6 bg-[#002FA7] text-white font-bold text-[10px]">{data.sw}</span></td>
                            <td className="py-1.5 px-3 text-center font-bold">{data.office + data.sw}</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setAutoPlanResult(null)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
                  <Button disabled={applyingPlan} onClick={handleAutoPlanApply}
                    className="flex-1 bg-[#22C55E] text-white hover:bg-[#16A34A] rounded-none" data-testid="apply-plan-btn">
                    {applyingPlan ? 'Applicazione...' : `Applica ${autoPlanResult.total_suggestions} Turni`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchedulesPage;
